import { EngagementBuyerActionState } from '../../../slices/marketplace/timeline/models/engagement-buyer-action-state.model';
import { TimelineActionStatus } from '../../../slices/marketplace/timeline/models/enums/timeline-action-status.enum';
import {
    TimelineEventStatus,
    TimelineEventStatuses,
    TimelineEventStatusesColorCode,
} from '../../../slices/marketplace/timeline/models/enums/timeline-event-status.enum';
import {
    TimelineParty,
    TimelinePartyColors,
} from '../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import { TimelineEvent } from '../../../slices/marketplace/timeline/models/timeline-event.model';
import { TimelineStage } from '../../../slices/marketplace/timeline/models/timeline-stage.model';
import { GanttChartItem } from '../../GanttChartComponent/models/GanttChartItem.model';
import { getProjectedTransitionDateUtc } from '../../../slices/marketplace/timeline/utils/timeline-dates';
import { STAGE_ITEMS } from '../Constants/dropdownItems';
import { DateTimeHelper } from '../../../utils/dateTimeHelper';
import {
    breakEndUtc,
    isBreakMarker,
    startNotBeforeTheOpenBreak,
} from '../../../slices/marketplace/timeline/utils/break-markers';
import {
    GanttRowInProgress,
    GanttRowProgress,
    inOrderOfProgress,
    progressOf,
    startNotBeforeNow,
} from '../../../slices/marketplace/timeline/utils/gantt-rows';

const BREAK_MARKER_COLOR = '#6c757d';
const ENGAGEMENT_FIRST_DAY = 0;

type GanttRow = GanttRowInProgress & { item: GanttChartItem };

interface TimelineSummary {
    stage: number;
    backgroundColor: string;
    dayStart: number;
    dayEnd: number;
    startDate?: Date;
    endDate?: Date;
}

export class DataGenerator {
    generateData(
        stages: TimelineStage[],
        events: TimelineEvent[],
        authorizedParty: TimelineParty,
        buyerActionState: EngagementBuyerActionState[],
        isEventClickable?: (eventId: string) => boolean,
        engagementStartDate?: Date,
        nowUtc?: Date,
        drawsAsOfNow = false
    ) {
        let data: GanttChartItem[] = [];
        const grouppedTimelineEvents = this.groupTimelineEvents(
            events.filter((event) => !isBreakMarker(event)),
            engagementStartDate
        );

        this.addPartyData(
            data,
            stages,
            events,
            grouppedTimelineEvents,
            authorizedParty,
            buyerActionState,
            isEventClickable,
            engagementStartDate,
            nowUtc,
            drawsAsOfNow
        );

        return data;
    }

    private addPartyData(
        data: GanttChartItem[],
        stages: TimelineStage[],
        events: TimelineEvent[],
        grouppedTimelineEvents: Record<number, TimelineSummary>,
        authorizedParty: TimelineParty,
        buyerActionState: EngagementBuyerActionState[],
        isEventClickable?: (eventId: string) => boolean,
        engagementStartDate?: Date,
        nowUtc?: Date,
        drawsAsOfNow = false
    ) {
        if (events.length > 0) {
            const base = engagementStartDate ?? new Date();
            const timelineStartUtc = getProjectedTransitionDateUtc(base, 0);
            data.push({
                id: `Stages-header`,
                name: 'Stages',
                start: undefined,
                end: undefined,
                color: undefined,
                backgroundColor: undefined,
                orderNo: undefined,
                type: 'header',
                groupName: 'stages',
                isClickable: false,
                itemStatus: '',
                itemStatusColor: '',
                isBlinkAnimationActive: false,
            });

            const stageEntries = Object.values(grouppedTimelineEvents);

            // [#1307 follow-up, Roman 21-Oct] Only what starts on the engagement's first day is drawn from the chart's
            // origin. A catch-up's first steps start at the resume, months later, and are drawn from there.
            for (const i of stageEntries) {
                const isFirstStage = i.dayStart === ENGAGEMENT_FIRST_DAY;
                const stageStart = isFirstStage
                    ? timelineStartUtc
                    : i.startDate ?? getProjectedTransitionDateUtc(base, i.dayStart);
                const stageEnd =
                    i.endDate ?? getProjectedTransitionDateUtc(base, i.dayEnd);
                const stage = stages.find((s) => s.id == i.stage);
                if (!stage) continue;
                data.push({
                    id: `stage-${i.stage}`,
                    name: stage.name,
                    start: stageStart,
                    end: stageEnd,
                    color: 'black',
                    backgroundColor: i.backgroundColor,
                    orderNo: undefined,
                    type: 'header',
                    groupName: 'stages',
                    isClickable: false,
                    itemStatus: '',
                    itemStatusColor: '',
                    isBlinkAnimationActive: false,
                });
            }

            const rows: GanttRow[] = [];
            const nowOrTodayUtc = nowUtc ?? new Date();
            for (let event of events) {
                // [#1298 / #1300] A break marker is a bar for the whole break - from the moment it fell until this
                // timeline moved again, up to today while it lasts - not a step to click. "Today" is the engagement's
                // simulated clock when one is set, as for the current-day line.
                if (isBreakMarker(event)) {
                    const brokeAtUtc = DateTimeHelper.parseUtc(event.startedAt);
                    rows.push({
                        progress: GanttRowProgress.Done,
                        startUtc: brokeAtUtc,
                        item: {
                            id: `event-${event.id}`,
                            name: event.name,
                            start: brokeAtUtc,
                            end: breakEndUtc(event, nowOrTodayUtc),
                            color: 'black',
                            backgroundColor: BREAK_MARKER_COLOR,
                            type: 'line',
                            isClickable: false,
                            itemStatus: '',
                            itemStatusColor: 'black',
                            isBlinkAnimationActive: false,
                        },
                    });
                    continue;
                }

                const plannedStart =
                    event.dayStart === ENGAGEMENT_FIRST_DAY
                        ? timelineStartUtc
                        : event.startedAt
                          ? DateTimeHelper.parseUtc(event.startedAt)
                          : getProjectedTransitionDateUtc(base, event.dayStart);
                const eventStart = drawsAsOfNow
                    ? startNotBeforeNow(event, plannedStart, nowOrTodayUtc)
                    : startNotBeforeTheOpenBreak(event, plannedStart, events, nowOrTodayUtc);
                const plannedEnd = event.completedAt
                    ? DateTimeHelper.parseUtc(event.completedAt)
                    : event.startedAt
                      ? getProjectedTransitionDateUtc(
                            DateTimeHelper.parseUtc(event.startedAt),
                            event.currentTempoDurationDays
                        )
                      : getProjectedTransitionDateUtc(base, event.dayEnd);
                const eventEnd = plannedEnd < eventStart ? eventStart : plannedEnd;
                const backgroundColor = TimelinePartyColors.find(
                    (p) => p.id === event.party
                )?.backgroundColor;
                // [#1300] A step the suspension cancelled is history: greyed up to the break, never clickable.
                const isCancelled = event.status === TimelineEventStatus.Cancelled;
                const isClickable =
                    !isCancelled && !!isEventClickable && isEventClickable(event.id);
                const color =
                    event.status !== TimelineEventStatus.Completed && isClickable
                        ? '#545cd8'
                        : 'black';
                const isBuyerPartyEvent =
                    event.party === TimelineParty.Buyer ||
                    event.party === TimelineParty.SellerAndBuyer;
                const isBuyerCompletedEvent =
                    authorizedParty === TimelineParty.Buyer &&
                    isBuyerPartyEvent &&
                    event.linkedAction != null &&
                    buyerActionState.some(
                        (as) =>
                            as.actionId === event.linkedAction.id &&
                            as.status === TimelineActionStatus.Completed
                    );
                const eventStatus = isBuyerCompletedEvent
                    ? TimelineEventStatus.Completed
                    : event.isSuspendEvent &&
                        event.status === TimelineEventStatus.Late
                      ? TimelineEventStatus.InProgress
                      : event.status;

                rows.push({
                    progress: progressOf(eventStatus),
                    startUtc: eventStart,
                    item: {
                        id: `event-${event.id}`,
                        name: event.name,
                        start: eventStart,
                        end: eventEnd,
                        color: color,
                        backgroundColor:
                            event.isSuspendEvent || isCancelled
                                ? '#ced4da'
                                : backgroundColor,
                        type: 'line',
                        isClickable,
                        itemStatus:
                            TimelineEventStatuses.find(
                                (f) =>
                                    f.id === eventStatus &&
                                    f.id !== TimelineEventStatus.Upcoming
                            )?.name ?? '',
                        itemStatusColor:
                            TimelineEventStatusesColorCode.find(
                                (f) => f.id === eventStatus
                            )?.color ?? 'black',
                        isBlinkAnimationActive: this.isEventBlinkAnimationActive(
                            event,
                            authorizedParty,
                            buyerActionState
                        ),
                    },
                });
            }

            const orderedRows = drawsAsOfNow ? inOrderOfProgress(rows) : rows;
            orderedRows.forEach((row, index) => data.push({ ...row.item, orderNo: index + 1 }));
        }
    }

    private isEventBlinkAnimationActive(
        event: TimelineEvent,
        authorizedParty: TimelineParty,
        buyerActionState: EngagementBuyerActionState[]
    ): boolean {
        if (event.party !== authorizedParty || !!!event.linkedAction) {
            return false;
        }

        if (authorizedParty === TimelineParty.Buyer) {
            const doesBuyerCompleteEvent = buyerActionState.some(
                (actionState) =>
                    actionState.actionId === event?.linkedAction?.id &&
                    actionState.status === TimelineActionStatus.Completed
            );

            return (
                event.status === TimelineEventStatus.Late &&
                !doesBuyerCompleteEvent
            );
        }

        return event.status === TimelineEventStatus.Late;
    }

    groupTimelineEvents(
        events: TimelineEvent[],
        base?: Date
    ): Record<number, TimelineSummary> {
        const result: Record<number, TimelineSummary> = {};
        const stageColors = new Map<number, string>();

        events.forEach((event) => {
            const { stage, dayStart, dayEnd, startedAt, completedAt } = event;

            const eventStartDate = base
                ? (startedAt ? DateTimeHelper.parseUtc(startedAt) : getProjectedTransitionDateUtc(base, dayStart))
                : undefined;
            const eventEndDate = base
                ? completedAt
                    ? DateTimeHelper.parseUtc(completedAt)
                    : startedAt
                      ? getProjectedTransitionDateUtc(
                            DateTimeHelper.parseUtc(startedAt),
                            event.currentTempoDurationDays
                        )
                      : getProjectedTransitionDateUtc(base, dayEnd)
                : undefined;

            if (!stageColors.has(stage)) {
                const color = STAGE_ITEMS[stageColors.size % STAGE_ITEMS.length].color;
                stageColors.set(stage, color);
            }

            if (!result[stage]) {
                result[stage] = {
                    stage,
                    backgroundColor: stageColors.get(stage),
                    dayStart,
                    dayEnd,
                    startDate: eventStartDate,
                    endDate: eventEndDate,
                };
            } else {
                result[stage].dayStart = Math.min(result[stage].dayStart, dayStart);
                result[stage].dayEnd = Math.max(result[stage].dayEnd, dayEnd);
                if (eventStartDate && (!result[stage].startDate || eventStartDate < result[stage].startDate)) {
                    result[stage].startDate = eventStartDate;
                }
                if (eventEndDate && (!result[stage].endDate || eventEndDate > result[stage].endDate)) {
                    result[stage].endDate = eventEndDate;
                }
            }
        });

        return result;
    }
}
